import axios from "axios";

const SESSION_KEY = "formula_session";
const SESSION_EVENT = "formula-session-updated";
let refreshRequest = null;

function storedSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function publishSession(session) {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail: session }));
}

/**
 * Professional API Service for Formula Soccer Application
 * Handles authentication, error logging, and standardized requests
 */
export const apiService = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api/v1",
  timeout: 15000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * Request Interceptor - Attaches JWT token and logs requests
 */
apiService.interceptors.request.use(
  (config) => {
    // Retrieve token from localStorage (adjust based on your auth implementation)
    const token = storedSession()?.accessToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Development logging
    if (process.env.NODE_ENV === "development") {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  async (error) => {
    // Handle request errors
    if (process.env.NODE_ENV === "development") {
      console.error("[API Request Error]", error);
    }
    const original = error.config;
    const authRequest = String(original?.url || "").includes("/auth/");
    if (error.response?.status === 401 && original && !original._formulaRetried && !authRequest) {
      original._formulaRetried = true;
      try {
        if (!refreshRequest) {
          refreshRequest = apiService.post("/auth/refresh", undefined, { _formulaRetried: true })
            .then((response) => {
              const current = storedSession();
              const next = { user: current?.user || null, accessToken: response.data.accessToken };
              publishSession(next);
              return response.data.accessToken;
            })
            .finally(() => { refreshRequest = null; });
        }
        const token = await refreshRequest;
        original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
        return apiService(original);
      } catch (refreshError) {
        publishSession(null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - Handles responses and errors
 */
apiService.interceptors.response.use(
  (response) => {
    // Development logging
    if (process.env.NODE_ENV === "development") {
      console.log(`[API Response] ${response.config?.method?.toUpperCase()} ${response.config?.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  (error) => {
    // Development logging
    if (process.env.NODE_ENV === "development") {
      console.error(`[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        message: error.message,
      });
    }

    // Handle common HTTP errors
    if (error.response) {
      const { status } = error.response;

      // Handle specific status codes as needed
      switch (status) {
        case 400:
          // Bad request - validation errors, etc.
          console.warn("Bad request:", error.response.data);
          break;
        case 401:
          // Unauthorized - token expired or invalid
          // You might want to clear auth state and redirect to login
          // Example: localStorage.removeItem("accessToken");
          //          window.dispatchEvent(new Event("auth-expired"));
          break;
        case 403:
          // Forbidden - insufficient permissions
          console.warn("Access forbidden:", error.response.data);
          break;
        case 404:
          // Not found
          console.warn("Resource not found:", error.config.url);
          break;
        case 429:
          // Rate limit exceeded
          console.warn("Rate limit exceeded");
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          console.error("Server error:", error.response.data);
          break;
        default:
          break;
      }
    } else if (error.request) {
      // Network error or CORS issue
      console.error("Network error - check API server and CORS settings:", error.message);
    } else {
      // Request setup error
      console.error("Request setup error:", error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * API Helper Functions - Clean, consistent API calls
 */
export const api = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint (relative to baseURL)
   * @param {object} params - Query parameters
   * @returns {Promise} - Axios response
   */
  get: (endpoint, params = {}) => apiService.get(endpoint, { params }),

  /**
   * POST request
   * @param {string} endpoint - API endpoint (relative to baseURL)
   * @param {object} data - Request body data
   * @returns {Promise} - Axios response
   */
  post: (endpoint, data = {}) => apiService.post(endpoint, data),

  /**
   * PUT request
   * @param {string} endpoint - API endpoint (relative to baseURL)
   * @param {object} data - Request body data
   * @returns {Promise} - Axios response
   */
  put: (endpoint, data = {}) => apiService.put(endpoint, data),

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint (relative to baseURL)
   * @param {object} data - Request body data
   * @returns {Promise} - Axios response
   */
  patch: (endpoint, data = {}) => apiService.patch(endpoint, data),

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint (relative to baseURL)
   * @returns {Promise} - Axios response
   */
  delete: (endpoint) => apiService.delete(endpoint),
};

export default apiService;
